// components/views/claim/ClaimPage.jsx
import React, { Component } from 'react';
import { withRouter } from "react-router-dom";
import JobPreview from './JobPreview';
import ClaimForm from './ClaimForm';
import TermsAgreement from './TermsAgreement';
import { PublicHTTPService } from '../../../services/publicHttpService';
import ReactGA from 'react-ga4';

const PublicAPI = new PublicHTTPService();

class ClaimPage extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      currentStep: 1, // 1: Job Preview, 2: Terms Agreement, 3: Registration Form
      jobDetails: null,
      isValidClaim: false,
      loading: true,
      error: null,
      claimToken: null,
      requestId: null
    };
  }

  async componentDidMount() {
    ReactGA.initialize(process.env.REACT_APP_GA);
    ReactGA.send({ hitType: "pageview", page: "/claim" });

    // Extract query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const requestId = urlParams.get('request');
    const token = urlParams.get('token');

    if (!requestId || !token) {
      this.setState({
        loading: false,
        error: "Invalid claim link. Missing request ID or token."
      });
      return;
    }

    this.setState({ requestId, claimToken: token });

    try {
      // Validate the claim token and get job details
      const { data } = await PublicAPI.get('/claim', { request: requestId, token });
      
      if (data.error) {
        this.setState({
          loading: false,
          error: data.message,
          isValidClaim: false
        });
      } else {
        this.setState({
          loading: false,
          jobDetails: data.jobDetails,
          isValidClaim: true
        });
      }
    } catch (error) {
      console.error('Claim validation error:', error);
      
      let errorMessage = "Failed to validate claim link.";
      if (error.response?.status === 404) {
        errorMessage = error.response.data.claimed 
          ? "This job has already been claimed by another specialist."
          : "Invalid or expired claim link.";
      }
      
      this.setState({
        loading: false,
        error: errorMessage,
        isValidClaim: false
      });
    }
  }

  handleNext = () => {
    this.setState(prevState => ({
      currentStep: prevState.currentStep + 1
    }));
  };

  handleBack = () => {
    this.setState(prevState => ({
      currentStep: prevState.currentStep - 1
    }));
  };

  handleClaimSubmit = async (prospectData) => {
    try {
      this.setState({ loading: true });

      const { data } = await PublicAPI.post('/claim/process', {
        requestId: this.state.requestId,
        token: this.state.claimToken,
        prospectData
      });

      if (data.error) {
        this.setState({
          loading: false,
          error: data.message
        });
      } else {
        // Success! Redirect to success page or login
        ReactGA.event({
          category: 'Prospect',
          action: 'Claimed Job',
          label: 'success'
        });
        
        alert("Job claimed successfully! You'll receive login credentials shortly.");
        // In a real app, you might redirect to a success page or login
        window.location.href = "/"; // or wherever you want to send them
      }
    } catch (error) {
      console.error('Claim processing error:', error);
      
      let errorMessage = "Failed to claim job. Please try again.";
      if (error.response?.status === 409) {
        errorMessage = "This job has already been claimed by another specialist.";
      }
      
      this.setState({
        loading: false,
        error: errorMessage
      });
    }
  };

  render() {
    const { currentStep, loading, error, isValidClaim, jobDetails } = this.state;

    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex justify-content-center align-items-center">
          <div className="max-w-md mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                <svg className="animate-spin w-8 h-8 text-teal-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Validating Claim Link
              </h3>
              <p className="text-gray-600" style={{fontFamily: 'Lato, sans-serif'}}>
                Please wait while we verify your job opportunity...
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (error || !isValidClaim) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Claim Unavailable
              </h3>
              <p className="text-gray-600 mb-6" style={{fontFamily: 'Lato, sans-serif'}}>
                {error}
              </p>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-700" style={{fontFamily: 'Lato, sans-serif'}}>
                  If you believe this is an error, please contact support or check your email for the correct claim link.
                </p>
              </div>
              <div className="mt-6">
                <a 
                  href="/" 
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium rounded-lg transition-colors"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                  </svg>
                  Return to Jobvious
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Progress Header - Only show during the flow, not on individual pages */}
        {currentStep > 1 && (
          <div className="bg-white border-b sticky top-0 z-10">
            <div className="max-w-md mx-auto px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-sm font-medium text-gray-700" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Job Opportunity
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3].map((step) => (
                    <div
                      key={step}
                      className={`w-2 h-2 rounded-full ${
                        step === currentStep 
                          ? 'bg-teal-500' 
                          : step < currentStep 
                            ? 'bg-green-500' 
                            : 'bg-gray-300'
                      }`}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        {currentStep === 1 && (
          <JobPreview 
            jobDetails={jobDetails}
            onNext={this.handleNext}
          />
        )}
        
        {currentStep === 2 && (
          <TermsAgreement 
            jobDetails={jobDetails}
            onNext={this.handleNext}
            onBack={this.handleBack}
          />
        )}
        
        {currentStep === 3 && (
          <ClaimForm 
            jobDetails={jobDetails}
            onSubmit={this.handleClaimSubmit}
            onBack={this.handleBack}
            loading={this.state.loading}
            error={this.state.error}
          />
        )}
      </div>
    );
  }
}

export default withRouter(ClaimPage);